import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiCopy, FiUsers } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

export default function LobbyView({ challenge, challengeId, onPhaseChange }) {
  const { participants, participantCount } = useLiveChallenge();
  const [dbParticipants, setDbParticipants] = useState([]);
  const [starting, setStarting] = useState(false);

  const accessCode = challenge?.access_code || '';
  const qrUrl = `${window.location.origin}/desafio-ao-vivo/entrar?code=${accessCode}`;

  // Combina lista do WebSocket com a do DB, deduplicando por id/user_id/name
  const allParticipants = (() => {
    const map = new Map();
    [...dbParticipants, ...participants].forEach(p => {
      const key = p.user_id || p.id || p.user_name || p.name;
      if (key && !map.has(key)) map.set(key, p);
    });
    return Array.from(map.values());
  })();
  const totalParticipants = Math.max(participantCount, allParticipants.length);

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const response = await liveChallengesApi.leaderboard(challengeId, 100);
        const items = response?.data ?? [];
        const normalized = (Array.isArray(items) ? items : []).map(it => {
          const attrs = it?.attributes || {};
          const userObj = attrs.user || {};
          return {
            id: it.id,
            user_id: userObj.id,
            user_name: userObj.name,
            classrooms: userObj.classrooms || [],
            score: attrs['total-score'] ?? attrs.total_score ?? 0
          };
        });
        setDbParticipants(normalized);
      } catch {
        // ignore
      }
    };
    fetchParticipants();
    const interval = setInterval(fetchParticipants, 5000);
    return () => clearInterval(interval);
  }, [challengeId]);

  const handleStart = async () => {
    if (totalParticipants === 0) {
      toast.error('Aguarde pelo menos um participante entrar.');
      return;
    }

    setStarting(true);
    try {
      await liveChallengesApi.start(challengeId);
      toast.success('Desafio iniciado!');
      if (onPhaseChange) onPhaseChange('questionActive');
    } catch {
      toast.error('Erro ao iniciar desafio.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 py-8 md:grid-cols-2 md:gap-12">
      {/* Coluna 1: QR + código + link */}
      <div className="flex flex-col items-center gap-6">
        <header className="flex flex-col items-center gap-2 text-center">
          <h2 className="m-0 text-2xl font-bold text-foreground">{challenge?.title || 'Desafio ao Vivo'}</h2>
          <p className="m-0 text-sm text-neutral">Escaneie o QR Code ou compartilhe o código.</p>
        </header>

        <Card
          border
          orientation="vertical"
          className="px-8 py-8"
        >
          <QRCodeSVG
            value={qrUrl || ' '}
            size={240}
            level="H"
            includeMargin
          />
        </Card>

        <div className="text-center">
          <p className="mb-1 text-sm font-semibold text-neutral">Código de Acesso</p>
          <p className="m-0 font-mono text-4xl font-extrabold tracking-widest text-primary-dark">{accessCode}</p>
        </div>

        {qrUrl && (
          <div className="w-full">
            <p className="mb-2 text-center text-sm font-semibold text-neutral">Link de Compartilhamento</p>
            <div className="flex items-center gap-2 rounded-lg border border-solid border-neutral-lightest bg-bright px-4 py-2">
              <span className="flex-1 truncate text-sm text-neutral-dark">{qrUrl}</span>
              <Button
                type="button"
                variant="default"
                size="xs"
                onClick={() => {
                  navigator.clipboard.writeText(qrUrl);
                  toast.success('Link copiado!');
                }}
              >
                <FiCopy /> Copiar
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Coluna 2: Participantes + ação */}
      <div className="flex flex-col gap-6">
        <Card
          border
          orientation="vertical"
          className="flex-1 px-5"
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-foreground">
              <FiUsers className="text-primary" /> Participantes
            </h3>
            <Badge
              variant="primary-light"
              size="default"
            >
              {totalParticipants}
            </Badge>
          </div>

          {allParticipants.length > 0 ? (
            <ul className="m-0 max-h-[420px] list-none space-y-1 overflow-y-auto pl-0">
              {allParticipants.map((p, idx) => {
                const name = p.user_name || p.name || `Participante ${idx + 1}`;
                const classrooms = Array.isArray(p.classrooms) ? p.classrooms.filter(Boolean) : [];
                const initials = name
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map(n => n[0]?.toUpperCase())
                  .join('');
                return (
                  <li
                    key={p.user_id || p.id || idx}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm odd:bg-bright"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-lightest text-xs font-bold text-primary-dark">{initials || '?'}</span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate font-semibold text-neutral-dark">{name}</span>
                      {classrooms.length > 0 && <span className="truncate text-xs text-neutral">{classrooms.join(' · ')}</span>}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="m-0 text-sm text-neutral-light">Aguardando participantes...</p>
          )}
        </Card>

        <Button
          type="button"
          variant="default"
          size="lg"
          fullWidth
          disabled={starting}
          onClick={handleStart}
        >
          {starting ? 'Iniciando...' : 'Iniciar desafio'}
        </Button>
      </div>
    </div>
  );
}
